import React, { useEffect, useState } from 'react'
import SafeIcon from '../../../common/SafeIcon'
import { supabase, supabaseConfigError } from '../../../config/supabase'
import { validateVideoUrl } from '../../../config/lessonService'
import * as FiIcons from 'react-icons/fi'

const { FiX, FiRefreshCw } = FiIcons

const CONTENT_TYPES = [
  { value: 'text_only', label: 'Text Only' },
  { value: 'video_only', label: 'Video Only' },
  { value: 'text_and_video', label: 'Text + Video' },
  { value: 'external_links', label: 'External Links' }
]

const SUBJECT_OPTIONS = [
  'Math',
  'Science',
  'Language Arts',
  'History/Social Studies',
  'Reading/Literature',
  'Physical Education',
  'Visual/Performing Arts',
  'Faith & Growth',
  'Enrichment'
]

const initialFormState = {
  title: '',
  description: '',
  subject: '',
  day_number: 1,
  order_index: 0,
  content: '',
  video_url: '',
  content_type: 'text_only',
  attachments: []
}

const LessonFormDialog = ({
  isOpen,
  onClose,
  courseId,
  curriculumId,
  totalDays = 180,
  lesson = null,
  onSave
}) => {
  const [formData, setFormData] = useState({ ...initialFormState })
  const [errors, setErrors] = useState({})
  const [saveError, setSaveError] = useState(null)
  const [saving, setSaving] = useState(false)

  const isEdit = Boolean(lesson?.id)
  const effectiveTotalDays = Math.max(1, Number(totalDays) || 180)

  useEffect(() => {
    if (!isOpen) return
    setSaveError(null)
    setErrors({})
    if (lesson) {
      setFormData({
        title: lesson.title ?? '',
        description: lesson.description ?? '',
        subject: lesson.subject ?? '',
        day_number: Number(lesson.day_number) || 1,
        order_index: Number(lesson.order_index) || 0,
        content: lesson.content ?? '',
        video_url: lesson.video_url ?? '',
        content_type:
          lesson.video_url && lesson.content
            ? 'text_and_video'
            : lesson.video_url
              ? 'video_only'
              : 'text_only',
        attachments: Array.isArray(lesson.attachments) ? lesson.attachments : []
      })
    } else {
      setFormData({
        ...initialFormState,
        day_number: 1,
        order_index: 0
      })
    }
  }, [isOpen, lesson])

  const validate = () => {
    const next = {}
    if (!formData.title?.trim()) next.title = 'Title is required'
    if (formData.title?.length > 200) next.title = 'Title must be 200 characters or less'
    if (!formData.subject?.trim()) next.subject = 'Subject is required'
    const day = parseInt(formData.day_number, 10)
    if (isNaN(day) || day < 1 || day > effectiveTotalDays) {
      next.day_number = `Day must be between 1 and ${effectiveTotalDays}`
    }
    if (formData.description?.length > 500) next.description = 'Description must be 500 characters or less'
    if (formData.video_url?.trim() && !validateVideoUrl(formData.video_url.trim())) {
      next.video_url = 'Enter a valid YouTube or Vimeo URL'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }))
  }

  const handleSave = async () => {
    if (supabaseConfigError) {
      setSaveError(supabaseConfigError.message)
      return
    }
    if (!validate()) return

    setSaving(true)
    setSaveError(null)

    const payload = {
      course_id: courseId,
      curriculum_id: curriculumId,
      title: formData.title.trim(),
      description: formData.description?.trim() || null,
      subject: formData.subject.trim(),
      day_number: parseInt(formData.day_number, 10),
      order_index: parseInt(formData.order_index, 10) || 0,
      content: formData.content?.trim() || null,
      video_url: formData.video_url?.trim() || null,
      attachments: Array.isArray(formData.attachments) ? formData.attachments : []
    }

    try {
      let result
      if (isEdit) {
        result = await supabase.from('lessons').update(payload).eq('id', lesson.id).select().single()
      } else {
        result = await supabase.from('lessons').insert([payload]).select().single()
      }
      if (result.error) throw result.error
      onSave?.()
      onClose()
    } catch (err) {
      setSaveError(err.message || 'Failed to save lesson')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const showContent = ['text_only', 'text_and_video'].includes(formData.content_type)
  const showVideo = ['video_only', 'text_and_video'].includes(formData.content_type)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEdit ? 'Edit Lesson' : 'Create Lesson'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (required)</label>
            <input
              type="text"
              maxLength={200}
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              placeholder="e.g., Introduction to Cells"
            />
            {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              maxLength={500}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              placeholder="Brief overview shown in lesson list"
            />
            {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject (required)</label>
            <select
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
            >
              <option value="">Select subject</option>
              {SUBJECT_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {errors.subject && <p className="text-sm text-red-600 mt-1">{errors.subject}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day number (required)</label>
              <input
                type="number"
                min={1}
                max={effectiveTotalDays}
                value={formData.day_number}
                onChange={(e) => handleChange('day_number', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              />
              <p className="text-xs text-gray-500 mt-1">Which school day (1–{effectiveTotalDays})</p>
              {errors.day_number && <p className="text-sm text-red-600 mt-1">{errors.day_number}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order within day</label>
              <input
                type="number"
                min={0}
                value={formData.order_index}
                onChange={(e) => handleChange('order_index', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              />
              <p className="text-xs text-gray-500 mt-1">0 = first on this day</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content type</label>
            <div className="flex flex-wrap gap-3">
              {CONTENT_TYPES.map(({ value, label }) => (
                <label key={value} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="content_type"
                    checked={formData.content_type === value}
                    onChange={() => handleChange('content_type', value)}
                    className="text-[#00AAFF] focus:ring-[#00AAFF]"
                  />
                  <span className="text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {showContent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                rows={6}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF] font-mono text-sm"
                placeholder="Lesson content (HTML or plain text). Sanitize before display."
              />
            </div>
          )}

          {showVideo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
              <input
                type="url"
                value={formData.video_url}
                onChange={(e) => handleChange('video_url', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="YouTube or Vimeo URL"
              />
              <p className="text-xs text-gray-500 mt-1">YouTube or Vimeo URL</p>
              {errors.video_url && <p className="text-sm text-red-600 mt-1">{errors.video_url}</p>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
            <p className="text-xs text-gray-500">File upload coming soon.</p>
          </div>

          {saveError && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
              {saveError}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5] disabled:opacity-60 flex items-center gap-2"
            >
              {saving && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Lesson'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LessonFormDialog
